/** Tags kept as-is in AI responses. Everything else is either dropped or unwrapped. */
const ALLOWED_TAGS = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "br",
  "hr",
  "ul",
  "ol",
  "li",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "del",
  "blockquote",
  "code",
  "pre",
  "a",
  "span",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td"
])

/** Tags removed together with their content — keeping their text would leak markup or scripts. */
const DROPPED_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "link",
  "meta",
  "base",
  "noscript",
  "template",
  "svg",
  "math",
  "form",
  "input",
  "button",
  "select",
  "textarea"
])

const ALLOWED_ATTRIBUTES = new Map<string, string[]>([["a", ["href", "title"]]])

const SAFE_URL = /^(https?:\/\/|mailto:|#|\/(?!\/))/i

function sanitizeAttributes(element: Element) {
  const tagName = element.tagName.toLowerCase()
  const allowed = ALLOWED_ATTRIBUTES.get(tagName) ?? []

  for (const { name } of Array.from(element.attributes)) {
    if (!allowed.includes(name.toLowerCase())) element.removeAttribute(name)
  }

  if (tagName !== "a") return

  const href = element.getAttribute("href")

  if (!href || !SAFE_URL.test(href.trim())) {
    element.removeAttribute("href")
    return
  }

  element.setAttribute("target", "_blank")
  element.setAttribute("rel", "noopener noreferrer")
}

function sanitizeChildren(parent: Element | DocumentFragment) {
  // Depth-first, so children of an unwrapped element are already clean when it is replaced.
  for (const child of Array.from(parent.children)) {
    sanitizeChildren(child)

    const tagName = child.tagName.toLowerCase()

    if (DROPPED_TAGS.has(tagName)) {
      child.remove()
      continue
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      child.replaceWith(...Array.from(child.childNodes))
      continue
    }

    sanitizeAttributes(child)
  }
}

/**
 * Strips unsafe markup from AI/chat HTML while keeping the formatting tags
 * (headings, lists, emphasis) that the prompts rely on for readable sections.
 */
export const sanitizeAiHtml = (html: string): string => {
  // <template> content is inert: no scripts run and no resources are fetched while parsing.
  const holder = document.createElement("template")
  holder.innerHTML = html

  sanitizeChildren(holder.content)

  return holder.innerHTML
}
